import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ResponseData } from '@freelancer/datastore';
import { FACEBOOK_CONFIG } from './facebook.config';
import { FacebookConfig } from './facebook.interface';

export interface FacebookUser {
  readonly email: string;
  readonly name: string;
  readonly profileUrl?: string;
}

@Injectable()
export class Facebook {
  isEnabled = false;
  loadedPromise: Promise<boolean>;

  /**
   * An existing successful login state which we track
   * because the Facebook SDK complains if we call login multiple times
   */
  successfulLoginState: ResponseData<fb.AuthResponse, fb.LoginStatus>;

  constructor(
    @Inject(FACEBOOK_CONFIG) private config: FacebookConfig,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    if (this.config.appId && isPlatformBrowser(this.platformId)) {
      this.isEnabled = true;
    }
  }

  get appId() {
    return this.config.appId;
  }

  async getUserDetails(
    userAuthResponse: fb.AuthResponse,
  ): Promise<FacebookUser | undefined> {
    if (!this.isEnabled) {
      return undefined;
    }

    await this.loadSdk();

    const userPromise = new Promise<any>((resolve, reject) => {
      window.FB.api(
        `/me`,
        {
          fields: 'email, first_name',
          access_token: userAuthResponse.accessToken,
        },
        response => {
          resolve(response);
        },
      );
    });
    const picPromise = new Promise<any>((resolve, reject) => {
      window.FB.api(
        `/me/picture`,
        {
          redirect: false,
          access_token: userAuthResponse.accessToken,
        },
        response => {
          resolve(response);
        },
      );
    });
    const userResponse = await userPromise;
    const picResponse = await picPromise;

    if (userResponse && !userResponse.error) {
      return {
        email: userResponse.email,
        name: userResponse.first_name,
        // don't block on not being able to fetch the profile pic
        profileUrl:
          picResponse && !picResponse.error ? picResponse.data.url : undefined,
      };
    }
  }

  async login(): Promise<ResponseData<fb.AuthResponse, fb.LoginStatus>> {
    if (!this.isEnabled) {
      return {
        status: 'error',
        errorCode: 'unknown',
      };
    }

    if (this.successfulLoginState) {
      return this.successfulLoginState;
    }

    const fbResponse = await new Promise<fb.StatusResponse>(
      (resolve, reject) => {
        window.FB.login(
          (response: fb.StatusResponse) => {
            resolve(response);
          },
          {
            scope: 'email, public_profile',
          },
        );
      },
    );

    if (fbResponse.status === 'connected' && fbResponse.authResponse) {
      this.successfulLoginState = {
        status: 'success',
        result: fbResponse.authResponse,
      };
      return this.successfulLoginState;
    }
    return {
      status: 'error',
      errorCode: fbResponse.status,
    };
  }

  loadSdk(): Promise<boolean> {
    if (this.loadedPromise) {
      return this.loadedPromise;
    }

    this.loadedPromise = new Promise((resolve, reject) => {
      const { appId } = this.config;
      if (!appId || !this.isEnabled) {
        resolve(false);
        return;
      }
      window.fbAsyncInit = () => {
        window.FB.init({
          appId,
          autoLogAppEvents: true,
          xfbml: false,
          cookie: false,
          status: true,
          version: 'v3.2',
        });
        // fetch the login status before resolving initialisation
        // this prevents popups from being blocked when we call `login` later.
        window.FB.getLoginStatus(status => {
          if (status.status === 'connected') {
            this.successfulLoginState = {
              status: 'success',
              result: status.authResponse,
            };
          }
          resolve(true);
        });
      };
      if (document.getElementById('external-fb-sdk')) {
        return;
      }
      const e = document.createElement('script');
      e.src = 'https://connect.facebook.net/en_US/sdk.js';
      e.id = 'external-fb-sdk';
      e.onerror = reject;
      const s = document.getElementsByTagName('script')[0];
      (s.parentNode as Node).insertBefore(e, s);
    });
    return this.loadedPromise;
  }
}
